/*  MUI  */
import { Box, FormControlLabel, Typography } from '@mui/material';

/*  Components  */
import { AuvikSwitch } from 'application/Shared/Components/Switches';

/* Shared */
import { Route, SessionHelper } from 'application/Shared/Helpers';
import { Env } from 'application/Shared/Utils';
import { Session } from 'application/Shared/types/session';
import { UserSelf } from 'application/Shared/types/user';
import { useUpdateSession } from 'hooks/redux/Selectors/session';

/* Objects */
import { User, UserFullName } from 'objects/User';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/*  Components  */
import { IconForSidebarListItem } from './SidebarIcons';
import { SidebarListItem } from './SidebarListItem';

/**
 * Component that renders user-specific menu items in the sidebar
 * @component
 * @param {Object} props - Component props
 * @param {string | undefined} props.page - Current active page identifier
 * @param {Session} props.session - User session object containing user preferences and permissions
 * @returns {JSX.Element} Sidebar menu items for user settings and logout
 */
export const SidebarUserMenuItems = ({
    page,
    session
}: {
    page: string | undefined;
    session: Session;
}): JSX.Element => {
    const { t } = useTranslation();
    const { clientId } = useParams();

    const updateSession = useUpdateSession();

    const sessionHelper = new SessionHelper(session);
    const tenant = sessionHelper.getTenant();

    const clientIdForLink = clientId || sessionHelper.getTenantId();
    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientIdForLink
    });
    const route =
        tenant.partner && sessionHelper.isPartner()
            ? '/home/configureSelfUser'
            : `${baseAdminRoute}/configureSelfUser`;

    return (
        <Box
            style={{ display: 'flex', flexDirection: 'column' }}
            data-testid="sidebarUserMenuItems"
            data-trackid="sidebarUserMenuItems"
            mt="4px"
            mb="8px"
        >
            {(Env.isStage() || Env.isLocal() || Env.isDev()) && (
                <Box ml="10px" my="12px">
                    <FormControlLabel
                        control={
                            <Box>
                                <AuvikSwitch
                                    checked={session?.user?.theme === 'dark'}
                                    onChange={() => {
                                        const theme =
                                            session?.user?.theme === 'dark'
                                                ? 'light'
                                                : 'dark';
                                        // NOTE: This is only used for testing and does not actually make a change to the user's session in the backend.
                                        // Therefore, we don't want to call `Session.getSessionInfo` like we do elsewhere.
                                        updateSession({
                                            ...session,
                                            user: {
                                                ...session.user,
                                                theme
                                            } as UserSelf
                                        });
                                    }}
                                    inputProps={{
                                        'aria-label': 'controlled'
                                    }}
                                />
                            </Box>
                        }
                        label={
                            <Box display="flex" alignItems="center">
                                <Typography
                                    sx={{
                                        color: 'sidebar.text',
                                        marginLeft: '8px'
                                    }}
                                >
                                    {t('Theme.darkMode')}
                                </Typography>
                            </Box>
                        }
                        style={{ marginLeft: 0 }}
                    />
                </Box>
            )}
            <User data={session.user!}>
                <SidebarListItem
                    bottomItem
                    icon={<IconForSidebarListItem src="Profile" />}
                    id="personal_settings"
                    name={
                        <Box style={{ overflowWrap: 'break-word' }}>
                            <UserFullName noTruncate useWhiteFont />
                        </Box>
                    }
                    permission="personal_settings"
                    route={route}
                    selected={page === 'personal'}
                />
            </User>
            <SidebarListItem
                bottomItem
                icon={<IconForSidebarListItem src="Logout" />}
                id="logout"
                name={t('Common.logout')}
                route="/logout"
            />
        </Box>
    );
};
